import {ReactNode, useCallback, useMemo, useState} from 'react'
import {TabFilter} from '@/features/measures/components/filters/tab-filter/TabFilter.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {BriefcaseIcon, FeatherIcon, LayersIcon, MapPinIcon, OctagonIcon, TagIcon} from '@components/ui/icon/Icon.tsx'
import {useTranslation} from 'react-i18next'
import useWindowDimensions from '@hooks/useWindowDimensions.ts'
import {FilterFieldNames, FilterFormValues, useFilterMeasures} from '@hooks/useFilterMeasures.tsx'
import {debounce, remapFiltersOptions} from '@utilities/helpers.ts'
import {MeasureFiltersDesktop} from '@components/ui/measure-filters/MeasureFiltersDesktop.tsx'
import {MeasureFiltersMobile} from '@components/ui/measure-filters/MeasureFiltersMobile.tsx'
import {useGetSections} from '@/features/measures/services/useGetSections.ts'
import {useGetHeadquarters} from '@/features/measures/services/useGetHeadquarters.ts'
import {useGetJudicialPanel} from '@/features/measures/services/useGetJudicialPanel.ts'
import {useGetInvolvedParties} from '@/features/measures/services/useGetInvolvedParties.ts'
import {useGetMeasuresSubTypes} from '@/features/measures/services/useGetMeasuresSubTypes.ts'
import {useGetGeneralRole} from '@/features/measures/services/useGetGeneralRole.ts'
import {useGetMeasuresTypes} from '@/features/measures/services/useGetMeasuresTypes.ts'

export const MeasureFilters = () => {
    const {t} = useTranslation()
    const {width} = useWindowDimensions()
    const isMobile = useMemo(() => width < 768, [width])

    const {filters, setFilters, filterSelected, filterCounter} = useFilterMeasures()

    const [searchSections, setSearchSections] = useState<string>('')
    const [searchSectionsHeadquarters, setSearchSectionsHeadquarters] = useState<string>('')
    const [searchSectionsPanel, setSearchSectionsPanel] = useState<string>('')
    const [searchSectionsInvolvedParties, setSearchSectionsInvolvedParties] = useState<string>('')

    const {
        remappedData: sections,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading: isLoadingSections
    } = useGetSections(searchSections)
    const {
        remappedData: headquarters,
        fetchNextPage: fetchNextPageHeadquarters,
        hasNextPage: hasNextPageHeadquarters,
        isFetchingNextPage: isFetchingNextPageHeadquarters,
        isLoading: isLoadingHeadquarters
    } = useGetHeadquarters(searchSectionsHeadquarters)
    const {
        remappedData: judicialPanel,
        fetchNextPage: fetchNextPagePanel,
        hasNextPage: hasNextPagePanel,
        isFetchingNextPage: isFetchingNextPagePanel,
        isLoading: isLoadingPanel
    } = useGetJudicialPanel(searchSectionsPanel)
    const {
        remappedData: involvedParties,
        fetchNextPage: fetchNextPageInvolvedParties,
        hasNextPage: hasNextPageInvolvedParties,
        isFetchingNextPage: isFetchingNextPageInvolvedParties,
        isLoading: isLoadingInvolvedParties
    } = useGetInvolvedParties(searchSectionsInvolvedParties)
    const {data: measuresTypes} = useGetMeasuresTypes()
    const {data: measuresSubTypes, isLoading: isLoadingMeasuresSubTypes} = useGetMeasuresSubTypes()
    const {data: generalRole, isLoading: isLoadingGeneralRole} = useGetGeneralRole()

    const debouncedSetFilters = useCallback(
        debounce((newFilters: Partial<FilterFormValues>) => {
            setFilters(newFilters)
        }, 50),
        [setFilters]
    )

    const filterFieldsDict: Record<
        Exclude<FilterFieldNames, 'activeTab' | 'page' | 'perPage'>,
        {
            label: string
            icon: ReactNode
            options: {id: string; name: string}[]
            isFetchingNextPage?: boolean
            fetchNextPage?: () => void
            hasNextPage?: boolean
            setSearchSections?: (value: string) => void
            isLoading?: boolean
        }
    > = {
        counter_part: {
            label: 'measures:filters:counter_part',
            icon: <OctagonIcon />,
            options: remapFiltersOptions(involvedParties),
            isFetchingNextPage: isFetchingNextPageInvolvedParties,
            fetchNextPage: fetchNextPageInvolvedParties,
            hasNextPage: hasNextPageInvolvedParties,
            setSearchSections: setSearchSectionsInvolvedParties,
            isLoading: isLoadingInvolvedParties
        },
        type_of_measure: {
            label: 'measures:filters:type_of_measure',
            icon: <LayersIcon />,
            options: measuresSubTypes?.data
                ?.filter((type: string) => {
                    if (filters.activeTab === 'all') return true
                    if (filters.activeTab === 'judgment') {
                        return ['judgment', 'short_judgment', 'judgment_device'].includes(type)
                    }
                    if (filters.activeTab === 'opinion') {
                        return ['opinion', 'interlocutory_opinion', 'final_opinion', 'suspensive_opinion'].includes(
                            type
                        )
                    }
                    if (filters.activeTab === 'decree') {
                        return [
                            'precautionary_decree',
                            'collegiate_decree',
                            'dispositive_decree',
                            'injunction_decree',
                            'presidential_decree',
                            'legal_aid'
                        ].includes(type)
                    }
                    if (filters.activeTab === 'ordinance') {
                        return [
                            'precautionary_order',
                            'collegial_order',
                            'injunction_order',
                            'presidential_order'
                        ].includes(type)
                    }
                    return true
                })
                .map((type: string) => ({
                    id: type,
                    name: t(`measures:type:${type}`)
                })),
            isLoading: isLoadingMeasuresSubTypes
        },
        general_role: {
            label: 'measures:filters:general_role',
            icon: <BriefcaseIcon />,
            options: generalRole?.data?.map((role: number) => ({id: String(role), name: String(role)})),
            isLoading: isLoadingGeneralRole
        },
        site: {
            label: 'measures:filters:site',
            icon: <MapPinIcon />,
            options: remapFiltersOptions(headquarters),
            isFetchingNextPage: isFetchingNextPageHeadquarters,
            fetchNextPage: fetchNextPageHeadquarters,
            hasNextPage: hasNextPageHeadquarters,
            setSearchSections: setSearchSectionsHeadquarters,
            isLoading: isLoadingHeadquarters
        },
        section: {
            label: 'measures:filters:section',
            icon: <TagIcon />,
            options: remapFiltersOptions(sections),
            isFetchingNextPage,
            fetchNextPage,
            hasNextPage,
            setSearchSections,
            isLoading: isLoadingSections
        },
        panel: {
            label: 'measures:filters:panel',
            icon: <FeatherIcon />,
            options: remapFiltersOptions(judicialPanel),
            isFetchingNextPage: isFetchingNextPagePanel,
            fetchNextPage: fetchNextPagePanel,
            hasNextPage: hasNextPagePanel,
            setSearchSections: setSearchSectionsPanel,
            isLoading: isLoadingPanel
        },
        general_number: {
            label: 'measures:filters:general_number',
            icon: <BriefcaseIcon />,
            options: [],
            isLoading: false
        }
    }

    return (
        <Flexbox align={isMobile ? 'center' : 'flex-start'} justify={'space-between'} fullWidth gap={4}>
            <TabFilter
                activeFiltersTab={filters.activeTab}
                setActiveFiltersTab={tab => {
                    const judgmentTypes = ['judgment', 'short_judgment', 'judgment_device']
                    const opinionTypes = ['opinion', 'interlocutory_opinion', 'final_opinion', 'suspensive_opinion']
                    const decreeTypes = [
                        'precautionary_decree',
                        'collegiate_decree',
                        'dispositive_decree',
                        'injunction_decree',
                        'presidential_decree',
                        'legal_aid'
                    ]
                    const ordinanceTypes = [
                        'precautionary_order',
                        'collegial_order',
                        'injunction_order',
                        'presidential_order'
                    ]

                    // Get current type_of_measure filters
                    const currentTypeFilters = filters.type_of_measure || []

                    if (tab === 'all') {
                        // Keep all current filters
                        setFilters({activeTab: tab})
                    } else if (tab === 'judgment') {
                        // Keep only judgment filters
                        const newTypeFilters = currentTypeFilters.filter(type => judgmentTypes.includes(type.id))
                        setFilters({
                            activeTab: tab,
                            type_of_measure: newTypeFilters
                        })
                    } else if (tab === 'opinion') {
                        // Keep only opinion filters
                        const newTypeFilters = currentTypeFilters.filter(type => opinionTypes.includes(type.id))
                        setFilters({
                            activeTab: tab,
                            type_of_measure: newTypeFilters
                        })
                    } else if (tab === 'decree') {
                        // Keep only decree filters
                        const newTypeFilters = currentTypeFilters.filter(type => decreeTypes.includes(type.id))
                        setFilters({
                            activeTab: tab,
                            type_of_measure: newTypeFilters
                        })
                    } else if (tab === 'ordinance') {
                        // Keep only ordinance filters
                        const newTypeFilters = currentTypeFilters.filter(type => ordinanceTypes.includes(type.id))
                        setFilters({
                            activeTab: tab,
                            type_of_measure: newTypeFilters
                        })
                    }
                }}
                options={measuresTypes?.data}
            />

            {isMobile ? (
                <MeasureFiltersMobile
                    filterFields={filterFieldsDict}
                    debouncedSubmit={debouncedSetFilters}
                    filterCounter={filterCounter}
                />
            ) : (
                <MeasureFiltersDesktop
                    filterFields={filterFieldsDict}
                    debouncedSubmit={debouncedSetFilters}
                    filterSelected={filterSelected}
                    watchedFilters={filters}
                />
            )}
        </Flexbox>
    )
}
